import { CheckCircle2, Circle, ClipboardCheck, RotateCcw, Sparkles, X } from 'lucide-react'

function OptionButton({ selected, disabled, label, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`w-full text-left px-3 py-3 sm:py-2.5 text-xs sm:text-sm border transition-all min-h-[44px] ${
        selected
          ? 'bg-cinema-glow/20 border-cinema-glow/50 text-white'
          : 'cinema-glass border-white/10 text-cinema-soft hover:border-cinema-glow/40 hover:text-white'
      } disabled:opacity-50`}
    >
      <span className="inline-flex items-center gap-2">
        {selected ? <CheckCircle2 size={14} className="text-cinema-glow" /> : <Circle size={14} className="text-cinema-muted" />}
        <span>{label}</span>
      </span>
    </button>
  )
}

function ResultBanner({ result }) {
  if (result === 'correct') {
    return <p className="text-emerald-400 text-sm font-medium">Correct</p>
  }
  if (result === 'partial') {
    return <p className="text-cinema-gold text-sm font-medium">Partly there</p>
  }
  if (result === 'unclear') {
    return <p className="text-cinema-muted text-sm font-medium">Let&apos;s clarify</p>
  }
  return <p className="text-amber-400 text-sm font-medium">Let&apos;s strengthen this</p>
}

export default function AITutorAssessment({
  assessment,
  submitting = false,
  lastResult = null,
  selectedAnswer = '',
  onSelectAnswer,
  onSubmit,
  onContinue,
  onPracticeAgain,
  onContinueLearning,
  onClose,
}) {
  if (!assessment) return null

  const current = assessment.currentQuestion
  const complete = assessment.status === 'completed'
  const questionNumber = current
    ? current.sequence
    : assessment.questionCount
  const isTrueFalse = current && current.type === 'true_false'
  const isChoice = current && (current.type === 'multiple_choice' || isTrueFalse)
  const options = isTrueFalse ? ['True', 'False'] : (current?.options || [])
  const canSubmit = Boolean(String(selectedAnswer || '').trim()) && !submitting && !lastResult

  if (complete) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-3 sm:px-4 border-b border-white/8 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <ClipboardCheck size={16} className="text-cinema-gold shrink-0" />
            <h2 className="font-display text-sm text-white truncate">{assessment.title || 'Your Result'}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 min-h-[44px] min-w-[44px] text-cinema-muted hover:text-white flex items-center justify-center" title="Close check" aria-label="Close check">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-8">
          <div className="max-w-md mx-auto cinema-glass-strong border border-white/10 p-4 sm:p-6 space-y-4">
            <p className="cinema-label text-xs">Your Result</p>
            <p className="text-xs uppercase tracking-wider text-cinema-gold font-medium">
              {assessment.label || 'Formative practice — not an official grade'}
            </p>
            <p className="font-display text-3xl text-white">
              {assessment.scoreCorrect} / {assessment.scoreTotal}
            </p>
            <div className="text-sm sm:text-base">
              <p className="text-white font-medium">{assessment.topic}</p>
              {(assessment.topicStatusBefore || assessment.topicStatusAfter) && (
                <p className="text-cinema-gold text-xs sm:text-sm mt-1 capitalize">
                  {(assessment.topicStatusBefore || 'Developing').replace('_', ' ')}
                  {assessment.topicStatusAfter ? ` → ${String(assessment.topicStatusAfter).replace('_', ' ')}` : ''}
                </p>
              )}
            </div>
            {assessment.recommendation?.label && (
              <div className="pt-3 border-t border-white/10">
                <p className="text-xs uppercase tracking-wider text-cinema-muted mb-1 font-medium">Recommended next step</p>
                <p className="text-sm sm:text-base text-cinema-soft">{assessment.recommendation.label}</p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 pt-2">
              <button
                type="button"
                onClick={onPracticeAgain}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs sm:text-sm cinema-btn-primary !min-h-[44px]"
              >
                <RotateCcw size={15} />
                Practice Again
              </button>
              <button
                type="button"
                onClick={onContinueLearning}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 min-h-[44px] text-xs sm:text-sm cinema-glass border border-white/10 text-cinema-soft hover:text-white"
              >
                Continue Learning
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 sm:px-4 border-b border-white/8 flex items-start sm:items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={16} className="text-cinema-gold shrink-0" />
            <h2 className="font-display text-sm sm:text-base text-white leading-snug">
              <span className="sm:hidden">{assessment.topic}</span>
              <span className="hidden sm:inline">{assessment.topic} — Quick Knowledge Check</span>
            </h2>
          </div>
          <p className="text-xs text-cinema-muted mt-1">
            {assessment.label || 'Formative practice — not an official grade'} · Question {questionNumber} of {assessment.questionCount}
          </p>
        </div>
        <button type="button" onClick={onClose} className="p-2 min-h-[44px] min-w-[44px] text-cinema-muted hover:text-white flex items-center justify-center shrink-0" title="Close check" aria-label="Close check">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">
        {current && (
          <div className="max-w-xl mx-auto space-y-4">
            <p className="text-base sm:text-lg text-white leading-relaxed font-normal">{current.question}</p>

            {isChoice ? (
              <div className="space-y-2">
                {options.map((option) => (
                  <OptionButton
                    key={option}
                    label={option}
                    selected={String(selectedAnswer).toLowerCase() === String(option).toLowerCase()}
                    disabled={Boolean(lastResult) || submitting}
                    onClick={() => onSelectAnswer && onSelectAnswer(option)}
                  />
                ))}
              </div>
            ) : (
              <textarea
                value={selectedAnswer}
                onChange={(e) => onSelectAnswer && onSelectAnswer(e.target.value.slice(0, 500))}
                disabled={Boolean(lastResult) || submitting}
                rows={4}
                placeholder="Type a short answer…"
                className="w-full bg-cinema-charcoal/80 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-cinema-muted/50 focus:outline-none focus:border-cinema-glow/50 disabled:opacity-50"
              />
            )}

            {lastResult && (
              <div className="cinema-glass border border-white/10 p-4 space-y-2">
                <ResultBanner result={lastResult.result} />
                <p className="text-sm text-cinema-soft leading-relaxed">
                  {lastResult.feedback || lastResult.explanation}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-3 sm:p-4 border-t border-white/10 flex items-center justify-stretch sm:justify-end gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {lastResult ? (
          <button
            type="button"
            onClick={onContinue}
            className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 w-full sm:w-auto text-xs sm:text-sm cinema-btn-primary !min-h-[44px]"
          >
            Continue
          </button>
        ) : (
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 w-full sm:w-auto text-xs sm:text-sm cinema-btn-primary !min-h-[44px] disabled:opacity-40"
          >
            <Sparkles size={14} />
            Submit Answer
          </button>
        )}
      </div>
    </div>
  )
}
